(ns graph-ui.web.backlog
  "The widget kind :backlog-band (IKC3/PLAN-ikc-v3-080-01/BACKLOG, screen 2
   of the plan). It is declared in
   dsl/contexts/ui/meta-ui/v2.1-SNAPSHOT/backlog-band.edn, and the
   declaration holds the reasons. This namespace draws it.

   ONE BAND: a heading, the bulk bar when a row is selected, and the rows.
   Each control sends ONE gesture with a map that the widget composes. The
   commands of the workflow decide, and a refusal is the sentence of the
   status line of the surface. So the band draws every control for every
   row, and it refuses nothing.

   THE SELECTION IS A STATE OF THE BROWSER, an atom of the band. It keeps
   only the ids that are still rows, so a row that left the band is never
   sent by the bulk bar.

   A ROW CROSSES A DRAG AS {item-id} under widgets/drag-mime, the MIME type
   of a dragged row of a :table. So a :box with a :drop takes it too.

   EVERY CHILD LIST IS SPREAD WITH `into`. A bare vector of hiccup as one
   child is read by Reagent as a component call (CLAUDE.md)."
  (:require [clojure.string :as str]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.widgets :as widgets]
            [reagent.core :as r]))

;; @plan PLAN-ikc-v3-080-01-the-tracker-v2
;; @adr ADR-graph-ui-v0-013-01-one-layout-model

(defn- fire!
  "Dispatch the command of `template` with `value` as $value."
  [bus session-id template value]
  (when (and bus template)
    (js-invoke bus "dispatch"
               (bind/fill-command template {'$session-id session-id '$value value})
               (fn [_] nil))))

(def ^:private muted "text-[var(--muted)]")
(def ^:private small-button
  (str "rounded border border-[var(--border)] px-2 py-0.5 text-xs whitespace-nowrap "
       "text-[var(--text)] bg-[var(--panel)] hover:bg-[var(--hover)]"))
(def ^:private primary-button
  "rounded px-2 py-0.5 text-xs whitespace-nowrap bg-[var(--accent)] text-white")
(def ^:private select-class
  (str "rounded border border-[var(--border)] px-1 py-0.5 text-xs "
       "text-[var(--text)] bg-[var(--panel)]"))

(def ^:private tone-frame
  {"danger"  "border-2 border-[var(--danger)]"
   "warning" "border-2 border-[var(--warning)]"})

(def ^:private tone-title
  {"danger"  "text-[var(--danger)]"
   "warning" "text-[var(--warning)]"})

(def kind-marks
  "The mark and the colour token of each work type."
  {"bug"             ["●" "var(--danger)"]
   "story"           ["▮" "var(--success)"]
   "task"            ["✓" "var(--accent)"]
   "epic"            ["⚡" "var(--accent)"]
   "feature-request" ["✦" "var(--warning)"]
   "decision"        ["◆" "var(--accent)"]
   "sub-task"        ["↳" "var(--muted)"]})

(def priority-marks
  "The mark of each priority, as the board draws it."
  {"highest" ["⇈" "var(--danger)"]
   "high"    ["↑" "var(--danger)"]
   "medium"  ["=" "var(--warning)"]
   "low"     ["↓" "var(--success)"]
   "lowest"  ["⇊" "var(--success)"]})

(def ^:private action-labels {"todo" "Todo" "backlog" "Backlog" "close" "Close…"})

(defn- mark [[glyph colour] title]
  [:span {:class "inline-block w-4 text-center shrink-0" :style {:color colour} :title title} glyph])

(defn- drag-data [id]
  (js/JSON.stringify #js {"item-id" id}))

(defn- dragged-id [e]
  (let [s (.getData (.-dataTransfer e) widgets/drag-mime)]
    (when (seq s)
      (try (aget (js/JSON.parse s) "item-id") (catch :default _ nil)))))

(defn- carries-row? [e]
  (boolean (some #(= widgets/drag-mime %)
                 (array-seq (js/Array.from (or (some-> e .-dataTransfer .-types) #js []))))))

(defn- close-select
  "The choice Close…: a native select whose first option is the label."
  [resolutions on-choose]
  (into [:select {:class select-class :value "" :aria-label "Close the work item with a resolution"
                  :on-change #(let [v (.. % -target -value)] (when (seq v) (on-choose v)))}
         [:option {:value ""} "Close…"]]
        (for [{v :value l :label} resolutions] ^{:key v} [:option {:value v} l])))

(def ^:private control-tags
  "The tags of the controls of a row. A press on one of them starts no drag."
  #{"SELECT" "OPTION" "INPUT" "BUTTON" "A" "LABEL"})

(defn drag-from?
  "True when a press on an element with the tag `tag-name` may drag the row."
  [tag-name]
  (not (contains? control-tags (some-> tag-name str/upper-case))))

(defn- arm-drag!
  "On a press in a row: the row is draggable only when the press is not on a
   control. IKC-298, George, 2026-10-24: in Firefox, the choice Close… of a
   draggable row did not open, and no command left the browser (the audit
   journal of the deployment). Firefox gives the press to the drag of the
   row before the select gets it. So a press on a control turns the drag off
   first, and the next press on the row turns it on again. React leaves the
   attribute alone, because the prop :draggable does not change."
  [e]
  (let [row (.-currentTarget e)]
    (set! (.-draggable row)
          (loop [el (.-target e)]
            (cond (or (nil? el) (identical? el row)) true
                  (not (drag-from? (.-tagName el))) false
                  :else (recur (.-parentElement el)))))))

(defn- row-view
  "One row of the band."
  [{:keys [id key kind title meta priority status similar missing]}
   {:keys [row-actions selectable rankable resolutions] :as props}
   selected? toggle! over-row bus session-id band-id]
  (let [{:keys [open move close rank]} (:on-gesture props)]
    (into
     ;; THE TITLE IS NEVER CUT (IKC-317, George, 2026-11-01). The title
     ;; wraps to the lines it needs. What does not fit goes under the
     ;; title — the meta shortens first, then the controls. The row keeps
     ;; at least 24ch for the title, and the title takes a line of its own
     ;; when the row is too narrow for one line. Before this the title
     ;; kept one line and a long title ended in an ellipsis. And before
     ;; the wrap a phone in landscape showed the key and one word of the
     ;; title, because the meta and the buttons held their width and
     ;; flex-1 starved the title to an ellipsis: a row of 272 px gave the
     ;; title 0 ch. The same rule holds in Your work
     ;; (graph-ui.web.your-work).
     [:div {:class (str "flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 border-t border-[var(--border)] text-sm min-w-0 "
                        (when selected? "bg-[var(--accent-soft)] "))
            :data-row id
            :draggable true
            :style (merge {:cursor "grab"} (widgets/drop-mark-style (= id @over-row)))
            :on-mouse-down arm-drag!
            :on-drag-start (fn [e]
                             (let [dt (.-dataTransfer e)]
                               (.setData dt widgets/drag-mime (drag-data id))
                               (.setData dt "text/x-ikc-band" band-id)
                               (set! (.-effectAllowed dt) "move")))
            :on-drag-over (fn [e]
                            (when (and rankable rank (carries-row? e))
                              (.preventDefault e)
                              (reset! over-row id)))
            :on-drag-leave (fn [_] (when (= id @over-row) (reset! over-row nil)))
            :on-drop (fn [e]
                       (when (and rankable rank (carries-row? e))
                         (let [from (.getData (.-dataTransfer e) "text/x-ikc-band")
                               drag (dragged-id e)]
                           (reset! over-row nil)
                           ;; A ROW OF THIS BAND IS RANKED. A row of another
                           ;; band falls through to the :drop of the band.
                           (when (= from band-id)
                             (.preventDefault e)
                             (.stopPropagation e)
                             (when (and drag (not= drag id))
                               (fire! bus session-id rank {:item-id drag :before-id id}))))))}]
     (concat
      [(if selectable
         [:input {:type "checkbox" :checked (boolean selected?) :class "shrink-0"
                  :aria-label (str "Select " (or key title))
                  :on-change (fn [_] (toggle! id))}]
         [:span {:class (str "shrink-0 " muted) :aria-hidden true} "⋮⋮"])
       [mark (get kind-marks kind ["•" "var(--muted)"]) kind]
       [:span {:class "font-mono text-xs font-semibold shrink-0"} (or key "—")]
       (into [:span {:class "flex-1 min-w-[24ch] break-words"}]
             (concat
              [[:a {:class (str "hover:underline " (when open "cursor-pointer"))
                    :title title
                    :on-click (fn [_] (fire! bus session-id open {:item-id id}))}
                title]]
              (when (seq similar)
                [[:span {:class "ml-2 text-[10px] font-semibold uppercase rounded border border-[var(--border)] px-1"
                         :data-similar "true"}
                  similar]])))
       (when (seq missing)
         (into [:span {:class "flex flex-wrap gap-1 shrink-0 max-w-[45%]" :data-missing "true"}]
               (for [m missing]
                 ^{:key m}
                 [:span {:class "text-[10px] font-semibold uppercase rounded border border-[var(--danger)] text-[var(--danger)] px-1"
                         :title "A move to Todo needs this field"}
                  m])))
       (when (seq meta) [:span {:class (str "text-xs min-w-[8ch] truncate " muted)} meta])
       (when (seq priority) [mark (get priority-marks priority ["" ""]) (str "Priority: " priority)])
       (when (seq status)
         [:span {:class "text-[10px] font-semibold uppercase rounded bg-[var(--hover)] px-1 shrink-0"} status])]
      (for [a (map name row-actions)]
        (case a
          "close" ^{:key a} [close-select resolutions #(fire! bus session-id close {:item-id id :resolution %})]
          ^{:key a} [:button {:class (if (= "todo" a) primary-button small-button)
                              :data-action a
                              :on-click (fn [_] (fire! bus session-id move {:item-id id :category a}))}
                     (get action-labels a a)]))))))

(defn- bulk-bar
  "The bar of the selected rows: Move to Todo, a priority, Close.

   THE CHOSEN PRIORITY (IKC-297, George, 2026-10-24: \"if none is selected
   it should default on medium\"). The choice starts on the entry of
   :priorities with :default true. A choice sets the priority of the
   selected items at once and keeps them selected, so Move to Todo is the
   next click. Move to Todo sends the chosen priority, and the server gives
   it to each item with none. Before, the choice cleared the selection and
   the bar went away, so a click seemed to do nothing."
  [_ids {:keys [priorities]} _clear! _bus _session-id]
  (let [chosen (r/atom (or (:value (first (filter :default priorities))) ""))]
    (fn [ids {:keys [resolutions priorities] :as props} clear! bus session-id]
      (let [bulk  (:bulk (:on-gesture props))
            fire* (fn [action value]
                    (fire! bus session-id bulk (cond-> {:item-ids (vec ids) :action action}
                                                 (seq value) (assoc :value value))))
            send! (fn [action value] (fire* action value) (clear!))]
        [:div {:class "flex flex-wrap items-center gap-2 px-3 py-1.5 bg-[var(--heading)] text-[var(--panel)] text-sm"
               :data-bulk-bar "true"}
         [:span (str (count ids) " selected")]
         [:button {:class small-button :on-click #(send! "todo" @chosen)} "Move to Todo"]
         [:label {:class "flex items-center gap-1"}
          [:span "Priority"]
          (into [:select {:class select-class :value @chosen
                          :aria-label "Set the priority of the selected work items"
                          :on-change #(let [v (.. % -target -value)]
                                        (reset! chosen v)
                                        (when (seq v) (fire* "priority" v)))}]
                (cond->> (for [{v :value l :label} priorities] ^{:key v} [:option {:value v} l])
                  (not-any? :default priorities) (cons ^{:key ""} [:option {:value ""} "None"])))]
         [close-select resolutions #(send! "close" %)]
         [:button {:class small-button :on-click clear!} "Clear"]]))))

(defn widget-backlog-band
  "One band of the backlog. Gestures :open :move :close :bulk :rank :drop."
  [_props _bus _session-id]
  (let [selected (r/atom #{})
        over-row (r/atom nil)
        over?    (r/atom false)
        band-id  (str (random-uuid))]
    (fn [{:keys [items title hint tone selectable accepts-drop empty-label] :as props} bus session-id]
      (let [ids    (set (map :id items))
            _      (when-not (every? ids @selected) (swap! selected #(set (filter ids %))))
            toggle! (fn [id] (swap! selected #(if (% id) (disj % id) (conj % id))))
            clear! #(reset! selected #{})
            drop   (:drop (:on-gesture props))
            tone   (some-> tone name)
            n      (or (:count props) (str (count items)))]
        (when (or (seq items) empty-label)
          [:div (merge {:class (str "flex flex-col rounded bg-[var(--panel)] overflow-hidden "
                                    (get tone-frame tone "border border-[var(--border)]"))
                        :data-band (or title "")
                        :style (widgets/drop-mark-style @over?)}
                       (when (and accepts-drop drop)
                         {:on-drag-enter (fn [e] (when (carries-row? e) (.preventDefault e) (reset! over? true)))
                          :on-drag-over  (fn [e] (when (carries-row? e) (.preventDefault e)))
                          :on-drag-leave (fn [e] (when (= (.-currentTarget e) (.-target e)) (reset! over? false)))
                          :on-drop (fn [e]
                                     (reset! over? false)
                                     (when (carries-row? e)
                                       (.preventDefault e)
                                       (when-let [id (dragged-id e)]
                                         (when-not (ids id)
                                           (fire! bus session-id drop {:item-id id})))))}))
           [:div {:class "flex items-baseline gap-2 px-3 py-2"}
            [:span {:class (str "font-semibold " (get tone-title tone "text-[var(--heading)]"))}
             (str title " · " n)]
            (when hint [:span {:class (str "text-xs " muted)} hint])]
           (when (and selectable (seq @selected))
             [bulk-bar (filter @selected (map :id items)) props clear! bus session-id])
           (if (empty? items)
             [:div {:class (str "px-3 py-2 text-sm border-t border-[var(--border)] " muted)} empty-label]
             (into [:div {:class "flex flex-col"}]
                   (for [row items]
                     ^{:key (:id row)}
                     [row-view row props (contains? @selected (:id row)) toggle! over-row
                      bus session-id band-id])))])))))
